<template>
    <header id="main-header" class="fixed top-0 z-50 w-full bg-transparent transition-colors duration-300">
        <nav class="flex gap-x-4 justify-between items-center p-4 mx-auto max-w-7xl lg:px-8 backdrop-blur-lg" aria-label="Global">
            <div class="flex shrink-0">
                <NuxtLink :to="localePath('/')" class="flex items-center p-1.5 -m-1.5">
                    <span class="sr-only">BestFaceSwap</span>
                    <img class="w-auto h-8" src="/idx/bestfaceswap-logo.svg" alt="">
                    <div class="hidden ml-4 text-2xl font-semibold text-white xl:block">BestFaceSwap</div>
                </NuxtLink>
            </div>
            <div class="flex lg:hidden">
                <NuxtLink :to="localePath('/')" class="ml-4 text-2xl font-semibold text-white">BestFaceSwap</NuxtLink>
            </div>
            <div class="flex lg:hidden">
                <button id="mobile-menu-button" type="button" class="inline-flex z-50 justify-center items-center p-2.5 -m-2.5 rounded-md text-neutral-500">
                    <span class="sr-only">Open main menu</span>
                    <img v-if="auth.isLogin" class="w-[30px] h-[30px] rounded-full" :src="comm.userInfo?.avatar || '/images/nobody.svg'" alt="">
                    <svg v-else class="w-6 h-6" fill="#ffffff" viewBox="0 0 24 24" stroke-width="2" stroke="#ffffff" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                </button>
            </div>
            <div class="hidden lg:flex lg:flex-1 lg:flex-nowrap lg:justify-end lg:items-center lg:gap-x-2 xl:gap-x-4 min-w-0">
                <NuxtLink to="#" @click.prevent="onClick" class="font-semibold leading-6 duration-300 cursor-pointer text-sm text-white/80 hover:text-white whitespace-nowrap">
                    {{ t('Face Swap') }}
                </NuxtLink>
                <NuxtLink :to="localePath('/ai-pose')" class="font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">
                    {{ t('AI Pose') }}
                </NuxtLink>
                <NuxtLink :to="localePath('/ai-video')" class="relative font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">
                    {{ t('AI Video') }}
                    <span class="absolute -top-1.5 -right-8 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow leading-none">HOT</span>
                </NuxtLink>
                <NuxtLink :to="localePath('/editor?type=seedance')" class="relative font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">
                    AI Studio
                    <span class="absolute -top-1.5 -right-8 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow leading-none">NEW</span>
                </NuxtLink>
                <NuxtLink :to="localePath('/image-to-video')" class="relative font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">
                    Image to Video
                    <span class="absolute -top-1.5 -right-8 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow leading-none">NEW</span>
                </NuxtLink>
                <NuxtLink :to="localePath('/editor?type=text-to-image')" class="font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">
                    Text to Image
                </NuxtLink>
                <NuxtLink :to="localePath('/editor?type=image-to-image')" class="font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">
                    Image to Image
                </NuxtLink>
                <NuxtLink :to="localePath('/pricing')" class="font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white whitespace-nowrap">{{ t('Pricing') }}
                </NuxtLink>

                <!-- App download button hidden - no longer guiding users to download app -->
                <!-- <AppDownloadButton v-if="!auth.isLogin" /> -->

                <!-- My History -->
                <template v-if="auth.isLogin">
                    <a href="#" @click.prevent="toggleRotation" class="flex items-center font-semibold leading-6 duration-300 text-sm text-white/80 hover:text-white group whitespace-nowrap">
                        {{ t('My History') }}
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-4 h-4 transition-transform duration-500 transform" :class="{ 'rotate-180': isRotated }" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                        <v-menu v-model="menuVisible" activator="parent" open-on-click>
                            <AccountPop @navigate="menuVisible = false"></AccountPop>
                        </v-menu>
                    </a>
                </template>

                <!-- App download button hidden - no longer guiding users to download app -->
                <!-- <AppDownloadButton v-if="auth.isLogin" /> -->
                <!-- Login status -->
                <template v-if="!auth.isLogin">
                    <NuxtLink :to="localePath('/login/google')" class="inline-flex gap-2 justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-opacity-90 rounded-full duration-300 group focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 bg-neutral-800 hover:text-white hover:bg-neutral-600 active:bg-neutral-500 whitespace-nowrap">
                        <span>{{ t('common.login') }}</span>
                    </NuxtLink>

                    <NuxtLink :to="localePath('/signup')" class="inline-flex gap-2 justify-center items-center px-4 py-2.5 text-sm font-semibold bg-orange-600 bg-opacity-90 rounded-full duration-300 group focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 text-neutral-950 hover:text-neutral-950 hover:bg-orange-700 active:bg-orange-600 whitespace-nowrap">
                        <span>{{ t('common.signup') }}</span>
                    </NuxtLink>
                </template>
                <template v-else>
                    <!-- avatar头像 -->
                    <v-menu>
                        <template v-slot:activator="{ props }">
                            <img @dragstart="event => event.preventDefault()" v-bind="props" :src="comm.userInfo?.avatar || '/images/nobody.svg'" class="object-cover w-10 h-10 rounded-full cursor-pointer" alt="" data-not-lazy>
                        </template>
                        <AccountInfo></AccountInfo>
                    </v-menu>
                </template>

            </div>
        </nav>
        <v-lazy>
            <Banner></Banner>
        </v-lazy>
        <!-- Mobile menu, show/hide based on menu open state. -->
        <div id="mobile-menu" class="hidden lg:hidden z-60" role="dialog" aria-modal="true">
            <div class="fixed inset-0 z-50 bg-black/60" @click="toggleMenu"></div>
            <div class="overflow-y-auto fixed inset-y-0 right-0 z-50 px-6 py-6 w-full bg-opacity-80 backdrop-blur-lg bg-neutral-950 sm:max-w-sm sm:ring-1 sm:ring-white/10">
                <div class="flex justify-between items-center">
                    <NuxtLink :to="localePath('/')" class="p-1.5 -m-1.5">
                        <span class="sr-only">BestFaceSwap</span>
                        <img class="w-auto h-8" src="/idx/bestfaceswap-logo.svg" alt="">
                    </NuxtLink>
                    <button id="close-menu-button" type="button" class="p-2.5 -m-2.5 text-white rounded-md">
                        <span class="sr-only">Close menu</span>
                        <svg class="w-6 h-6" fill="#ffffff" viewBox="0 0 24 24" stroke-width="2" stroke="#ffffff" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="flow-root mt-6">
                    <div class="-my-6 divide-y divide-gray-500/10">
                        <!-- 已登录：账户概览卡（头像 + 状态 + 积分 + faces），与桌面 AccountInfo 对齐 -->
                        <div v-if="auth.isLogin" class="py-6" @click="toggleMenu">
                            <div class="flex items-center gap-3 mb-4">
                                <img class="w-11 h-11 rounded-full object-cover shrink-0" :src="comm.userInfo?.avatar || '/images/nobody.svg'" alt="">
                                <div class="min-w-0 flex-1">
                                    <div class="text-white font-semibold truncate">{{ comm.userInfo?.nickname || comm.userInfo?.email || t('common.navigation.menu.account') }}</div>
                                    <span v-if="comm.userInfo?.user_status" class="inline-block mt-1 rounded-full text-black text-[11px] font-semibold px-2 py-0.5" :class="userStatusClass">{{ comm.userInfo?.user_status }}</span>
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <NuxtLink :to="localePath('/my/credits')" class="flex items-center justify-between rounded-xl bg-neutral-800 px-3 py-2.5 duration-300 hover:bg-neutral-700">
                                    <span class="flex items-center gap-1.5 text-sm text-white/70">
                                        <v-icon icon="mdi-star-face" size="18" />
                                        {{ t('common.navigation.menu.credits') }}
                                    </span>
                                    <span class="font-bold text-[#F06906]">{{ comm.userInfo?.credit_num || 0 }}</span>
                                </NuxtLink>
                                <NuxtLink :to="localePath('/my/faces')" class="flex items-center justify-between rounded-xl bg-neutral-800 px-3 py-2.5 duration-300 hover:bg-neutral-700">
                                    <span class="flex items-center gap-1.5 text-sm text-white/70">
                                        <v-icon icon="mdi-face-man" size="18" />
                                        {{ t('common.navigation.menu.faces') }}
                                    </span>
                                    <span class="font-bold text-white">{{ comm.userInfo?.counter_faces || 0 }}</span>
                                </NuxtLink>
                            </div>
                        </div>
                        <div class="py-6 space-y-2" @click="toggleMenu">
                            <template v-if="auth.isLogin">
                                <NuxtLink :to="localePath('/face-swap')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9 4a3 3 0 1 1 6 0 3 3 0 0 1-6 0Zm-3 14a6 6 0 1 1 12 0v1H6v-1Z" /></svg>
                                    {{ t('Face Swap') }}</NuxtLink>
                                <NuxtLink :to="localePath('/ai-pose')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0Zm-12 9a8 8 0 0 1 16 0v1H4v-1Z" /></svg>
                                    {{ t('AI Pose') }}</NuxtLink>
                                <NuxtLink :to="localePath('/ai-video')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h12v12H4V6Zm14 3 4-2v10l-4-2V9Z" /></svg>
                                    {{ t('AI Video') }}</NuxtLink>
                                <NuxtLink :to="localePath('/editor?type=seedance')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M15 3H9v2h6z"/></svg>
                                    AI Studio
                                    <span class="rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">NEW</span>
                                </NuxtLink>
                                <NuxtLink :to="localePath('/image-to-video')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h12v12H4V6Zm14 3 4-2v10l-4-2V9Z" /></svg>
                                    Image to Video
                                    <span class="rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">NEW</span>
                                </NuxtLink>
                                <NuxtLink :to="localePath('/editor?type=text-to-image')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                                    Text to Image
                                </NuxtLink>
                                <NuxtLink :to="localePath('/editor?type=image-to-image')" class="flex items-center justify-center gap-2 px-3 py-3 -mx-3 text-base font-semibold leading-7 rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                                    Image to Image
                                </NuxtLink>
                                <NuxtLink :to="localePath('/pricing')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('Pricing') }}</NuxtLink>
                                <NuxtLink :to="localePath('/my/account')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('Account') }}</NuxtLink>
                            </template>
                            <template v-else>
                                <NuxtLink :to="localePath('/face-swap-video')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('Video Face Swap') }}
                                </NuxtLink>
                                <NuxtLink :to="localePath('/face-swap-photo')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('Photo Face Swap') }}
                                </NuxtLink>
                                <NuxtLink :to="localePath('/ai-pose')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('AI Pose') }}
                                </NuxtLink>
                                <NuxtLink :to="localePath('/ai-video')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('AI Video') }}
                                </NuxtLink>
                                <NuxtLink :to="localePath('/editor?type=seedance')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    AI Studio <span class="ml-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">NEW</span>
                                </NuxtLink>
                                <NuxtLink :to="localePath('/image-to-video')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    Image to Video <span class="ml-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">NEW</span>
                                </NuxtLink>
                                <NuxtLink :to="localePath('/editor?type=text-to-image')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    Text to Image
                                </NuxtLink>
                                <NuxtLink :to="localePath('/editor?type=image-to-image')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    Image to Image
                                </NuxtLink>
                                <NuxtLink :to="localePath('/pricing')" class="block px-3 py-3 -mx-3 text-base font-semibold leading-7 text-center rounded-full duration-300 text-white/80 hover:text-white hover:bg-neutral-800">
                                    {{ t('Pricing') }}</NuxtLink>
                            </template>
                        </div>
                        <div class="py-6 text-center">
                            <template v-if="auth.isLogin">
                                <div @click="onLogout" class="inline-flex gap-2 justify-center items-center py-4 pr-6 pl-6 text-sm font-semibold text-white bg-opacity-90 rounded-full duration-300 group focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 bg-neutral-800 hover:text-white hover:bg-neutral-700 active:bg-neutral-600 active:text-gray-100 focus-visible:outline-gray-600">
                                    <span>{{ t('common.Log Out') }}</span>
                                </div>
                            </template>
                            <template v-else>
                                <div class="flex flex-col gap-y-6">
                                    <NuxtLink :to="localePath('/login/google')" class="inline-flex gap-2 justify-center items-center py-4 pr-4 pl-6 text-sm font-semibold text-white bg-opacity-90 rounded-full duration-300 group focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 bg-neutral-800 hover:text-white hover:bg-neutral-700 active:bg-neutral-600 active:text-gray-100 focus-visible:outline-gray-600">
                                        <span>{{ t('common.login') }}</span>
                                    </NuxtLink>

                                    <NuxtLink :to="localePath('/signup')" class="inline-flex gap-2 justify-center items-center py-4 pr-4 pl-6 text-sm font-semibold bg-orange-600 bg-opacity-90 rounded-full group focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 text-neutral-950">
                                        <span>{{ t('common.signup') }}</span>
                                    </NuxtLink>
                                </div>

                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>
<script setup lang="ts">
import AccountPop from './AccountPop.vue';
import AccountInfo from './AccountInfo.vue';
import Banner from './Banner.vue';
import type { MaterialWithStatus } from '~/types';
// App download buttons hidden - no longer guiding users to download app
// import GooglePlayButton from './GooglePlayButton.vue';
// import AppDownloadButton from './AppDownloadButton.vue';

const auth = useAuthStore();
const comm = useCommStore();
const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();

// 引用 DOM 元素
const menuButton = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLElement | null>(null);
const mobileMenu = ref<HTMLElement | null>(null);
const header = ref<HTMLElement | null>(null);
const menuVisible = ref(false);

// 切换菜单显示
const toggleMenu = () => {
    if (mobileMenu.value) {
        mobileMenu.value.classList.toggle('hidden');
        if (!mobileMenu.value.classList.contains('hidden')) {
            header.value?.classList.remove('backdrop-blur-lg');
        } else if (window.scrollY > 0) {
            header.value?.classList.add('backdrop-blur-lg');
        }
    }
};

// 滚动处理函数
const handleScroll = () => {
    if (window.scrollY > 0) {
        header.value?.classList.add('backdrop-blur-lg');
        mobileMenu.value?.classList.add('hidden');
    } else {
        if (header.value) {
            header.value.style.backgroundColor = 'transparent';
            header.value.classList.remove('backdrop-blur-lg');
        }
    }
};

onMounted(() => {
    menuButton.value = document.getElementById('mobile-menu-button') as HTMLElement;
    closeButton.value = document.getElementById('close-menu-button') as HTMLElement;
    mobileMenu.value = document.getElementById('mobile-menu') as HTMLElement;
    header.value = document.getElementById('main-header') as HTMLElement;


    menuButton.value?.addEventListener('click', toggleMenu);
    closeButton.value?.addEventListener('click', toggleMenu);

    window.addEventListener('scroll', handleScroll);
    handleScroll();
});

const onClick = () => {
    // 清空 matObj
    const matObj = useState<MaterialWithStatus>('matObj');
    matObj.value = null;
    router.push(localePath('/face-swap'));
};

const onLogout = () => {
    // 执行登出操作
    auth.logout();
};

const isRotated = ref(false);

const toggleRotation = () => {
    isRotated.value = !isRotated.value;
};

// 移动端账户卡状态色，与 AccountInfo 桌面弹窗保持一致
const userStatusClass = computed(() => {
    switch (comm.userInfo?.user_status) {
        case 'Free':
            return 'bg-green-500';
        case 'Premium':
            return 'bg-orange-500';
        case 'Expired':
            return 'bg-red-500';
        default:
            return 'bg-gray-500';
    }
});

watch(menuVisible, (newValue) => {
    if (!newValue) {
        isRotated.value = false;
    }
});

</script>
